/**
 * Moves engine integration layer.
 *
 * The moves engine (src/lib/moves) is UI-independent and does NOT voice its
 * results — see src/lib/moves/types.ts "Voicing". This helper closes that gap:
 * for every changed/inserted chord it re-runs the app's SATB voicing path,
 * EXCEPT where the move set voices directly and marked `voicesCustomized`
 * (let-the-bass-hold, walk-the-bass). It also recomputes each chord's on-canvas
 * position.x from its (possibly shifted) startBeat, matching the canvas
 * beat -> x convention (startBeat * GRID_BEAT_WIDTH * zoom).
 *
 * Pure: does not mutate its input, and does not touch the moves engine.
 */
import type { Chord, Voices } from '@/types';
import type { MoveResult } from '@/lib/moves';
import { generateSATBVoicing } from '@/audio/VoiceLeading';
import { CANVAS_CONFIG } from '@/utils/constants';

export function revoiceMoveResult(result: MoveResult, zoom: number): Chord[] {
  const changed = new Set(result.changedIds);
  const sorted = [...result.chords].sort((a, b) => a.startBeat - b.startBeat);
  const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;
  let previousVoicing: Voices | undefined;

  return sorted.map((chord) => {
    const positioned: Chord = {
      ...chord,
      position: { x: chord.startBeat * beatWidth, y: chord.position?.y ?? 0 },
    };

    // Revoice changed chords unless the move voiced them itself.
    if (changed.has(positioned.id) && !positioned.voicesCustomized) {
      const voices = generateSATBVoicing(positioned, previousVoicing);
      previousVoicing = voices;
      return { ...positioned, voices };
    }

    previousVoicing = positioned.voices;
    return positioned;
  });
}
